import type {ReactNode} from 'react';
import {Text, TextSize} from '@Components/text';
import type {UploadFileDataTypes, UserMediaType, UserMediaVOResponse} from '@Libraries/user-media-library';
import {getMediaStorageForbackendData, userMediaVOToElementData, uploadFiles} from '@Libraries/user-media-library';
import isArray from 'lodash/isArray';
import {onAddItems} from '@Components/poster-editor/library/poster-editor-open-modals';
import {isDirectlyAddableMediaData, isTempImageData} from '@Libraries/add-media-library';
import {noop} from '@Utils/general.util';
import {isUserLoggedIn} from '@Libraries/user.library';
import {updateUnlinkedMediaState} from '@Panels/user-media-panel/user-media-panel-reducer';
import styles from './poster-whiteboard-drag-drop-overlay.module.scss';
import {useAppDispatch} from '@/hooks';
import {Image} from 'react-bootstrap';
import {getAssetUrl} from '@Utils/s3.util';
import {DragDropOverlay} from '@Components/drag-drop-overlay/drag-drop-overlay';
import type {PosterWhiteboardDragDropOverlayProps} from '@Components/poster-editor/components/poster-whiteboard-drag-drop-overlay/poster-whiteboard-drag-drop-overlay.types';

export function PosterWhiteboardDragDropOverlay(props: PosterWhiteboardDragDropOverlayProps): ReactNode {
  const dispatch = useAppDispatch();

  const onMediaUploaded = (type: UserMediaType, data: UploadFileDataTypes): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }

    const items = isArray(data) ? data : [data];

    const tempItems = items.filter(isTempImageData);
    if (tempItems.length > 0) {
      onAddItems(tempItems);
    }

    const mediaResponses = items.filter((item): item is UserMediaVOResponse => !isDirectlyAddableMediaData(item));
    if (mediaResponses.length > 0) {
      if (!isUserLoggedIn()) {
        dispatch(updateUnlinkedMediaState(mediaResponses.map((item) => getMediaStorageForbackendData(item, type))));
      }

      onAddItems(mediaResponses.map((item) => userMediaVOToElementData(type, item)));
    }
  };

  const processAfterUpload = (type: UserMediaType, data: UserMediaVOResponse[] | UserMediaVOResponse): void => {
    if (!isUserLoggedIn()) {
      if (isArray(data)) {
        const mediaItems = data.map((item) => getMediaStorageForbackendData(item, type));
        dispatch(updateUnlinkedMediaState(mediaItems));
      } else {
        const mediaItem = getMediaStorageForbackendData(data, type);
        dispatch(updateUnlinkedMediaState([mediaItem]));
      }
    }
  };

  const onFilesUpload = (files: File[]): void => {
    void uploadFiles(
      {
        onFileUpload: onMediaUploaded,
        onUploadError: noop,
        onAfterUpload: processAfterUpload,
      },
      files,
      {shouldLoadDataUrlImage: true}
    );
  };

  const hoverContent = (
    <div className={`_full-width _full-height flex-center flex-v-row spacing-p-13 ${styles.content}`}>
      <Image src={getAssetUrl('drag-drop-overlay-image.png')} />
      <Text className="spacing-m-t-3 content-body-white" val={window.i18next.t('pmwjs_drag_drop_message_poster_whiteboard')} size={TextSize.SMALL} />
    </div>
  );

  return <DragDropOverlay targetElement={props.whiteboardContainerRef} onFilesUpload={onFilesUpload} onHoverContent={hoverContent} className={styles.container} />;
}
